import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import type { PointerStyle } from '../lib/types';

const POINTER_COLORS: { value: PointerStyle['color']; name: string }[] = [
  { value: '#ef4444', name: 'Red' },
  { value: '#f59e0b', name: 'Orange' },
  { value: '#22c55e', name: 'Green' },
  { value: '#3b82f6', name: 'Blue' },
  { value: '#a855f7', name: 'Purple' },
];
const SIZES: { value: PointerStyle['size']; name: string }[] = [
  { value: 'small', name: 'Small' },
  { value: 'medium', name: 'Medium' },
  { value: 'large', name: 'Large' },
];
const SHAPES: { value: PointerStyle['shape']; name: string }[] = [
  { value: 'dot', name: 'Dot' },
  { value: 'ring', name: 'Ring' },
  { value: 'arrow', name: 'Arrow' },
];

const DEFAULT_STYLE: PointerStyle = { color: '#ef4444', size: 'medium', shape: 'dot', stay: false };
const STORE_KEY = 'cg.pointerStyle';
/** At most this often (ms) while the parent moves it, so the child's screen keeps up. */
const SEND_EVERY = 40;

/** The parent's pointer look, remembered on this browser. */
export function usePointerStyle() {
  const [style, setStyle] = useState<PointerStyle>(() => {
    try {
      return { ...DEFAULT_STYLE, ...JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}') };
    } catch {
      return DEFAULT_STYLE;
    }
  });
  const update = (patch: Partial<PointerStyle>) =>
    setStyle((s) => {
      const next = { ...s, ...patch };
      try {
        localStorage.setItem(STORE_KEY, JSON.stringify(next));
      } catch {
        // Not saved; it still works for this visit.
      }
      return next;
    });
  return [style, update] as const;
}

/** Where a point on the video falls on the child's screen (0..1 each way), or null outside the picture. */
export function toScreen(video: HTMLVideoElement, clientX: number, clientY: number): { x: number; y: number } | null {
  const rect = video.getBoundingClientRect();
  const vw = video.videoWidth || rect.width;
  const vh = video.videoHeight || rect.height;
  // The picture is letterboxed inside the element (object-fit: contain).
  const scale = Math.min(rect.width / vw, rect.height / vh);
  const w = vw * scale;
  const h = vh * scale;
  const x = (clientX - rect.left - (rect.width - w) / 2) / w;
  const y = (clientY - rect.top - (rect.height - h) / 2) / h;
  return x < 0 || x > 1 || y < 0 || y > 1 ? null : { x, y };
}

/**
 * The layer over the child's screen that turns the parent's mouse or finger into the laser pointer.
 * Sends `{ x, y, ...style }` while moving (throttled) and `{ hide: true }` when it leaves the picture.
 */
export function PointerLayer({
  videoRef,
  style,
  send,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  style: PointerStyle;
  send: (m: object) => void;
}) {
  const [spot, setSpot] = useState<{ left: number; top: number } | null>(null);
  const last = useRef(0);
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shown = useRef(false);

  const hide = useCallback(() => {
    if (pending.current) clearTimeout(pending.current);
    pending.current = null;
    setSpot(null);
    if (shown.current) send({ hide: true });
    shown.current = false;
  }, [send]);

  // Hide it on the child's screen when the pointer is switched off or live view ends.
  useEffect(() => hide, [hide]);

  const move = (e: ReactPointerEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const at = toScreen(video, e.clientX, e.clientY);
    if (!at) {
      if (!style.stay) hide();
      return;
    }
    const layer = e.currentTarget.getBoundingClientRect();
    setSpot({ left: e.clientX - layer.left, top: e.clientY - layer.top });
    const message = { ...at, ...style };
    const go = () => {
      last.current = Date.now();
      pending.current = null;
      shown.current = true;
      send(message);
    };
    if (pending.current) clearTimeout(pending.current);
    const wait = SEND_EVERY - (Date.now() - last.current);
    if (wait <= 0) go();
    else pending.current = setTimeout(go, wait); // the last position always gets there
  };

  return (
    <div
      className="pointer-layer"
      onPointerMove={move}
      onPointerDown={move}
      onPointerLeave={() => !style.stay && hide()}
      aria-label="Point at the screen"
    >
      {spot && <span className={`pointer-spot ${style.shape} ${style.size}`} style={{ ...spot, ['--pointer' as string]: style.color }} />}
    </div>
  );
}

/** Color, size, shape and fade choices for the pointer. */
export function PointerOptions({ style, onChange }: { style: PointerStyle; onChange: (patch: Partial<PointerStyle>) => void }) {
  return (
    <div className="pointer-options" role="group" aria-label="Pointer look">
      <div className="swatches">
        {POINTER_COLORS.map((c) => (
          <button
            key={c.value}
            className={`swatch ${style.color === c.value ? 'picked' : ''}`}
            style={{ background: c.value }}
            onClick={() => onChange({ color: c.value })}
            aria-label={`${c.name} pointer`}
            aria-pressed={style.color === c.value}
          />
        ))}
      </div>
      <div className="segmented" role="group" aria-label="Pointer size">
        {SIZES.map((s) => (
          <button key={s.value} className={style.size === s.value ? 'active' : ''} onClick={() => onChange({ size: s.value })} aria-pressed={style.size === s.value}>
            {s.name}
          </button>
        ))}
      </div>
      <div className="segmented" role="group" aria-label="Pointer shape">
        {SHAPES.map((s) => (
          <button key={s.value} className={style.shape === s.value ? 'active' : ''} onClick={() => onChange({ shape: s.value })} aria-pressed={style.shape === s.value}>
            {s.name}
          </button>
        ))}
      </div>
      <label className="check compact">
        <input type="checkbox" checked={style.stay} onChange={(e) => onChange({ stay: e.target.checked })} />
        <span>Keep it showing</span>
      </label>
    </div>
  );
}
